import * as React from 'react';
import classnames from 'classnames';
import Base, { Props as BaseProps } from '../Base/Base';
import './TextConfigurations.css';
import './Text.css';

export type TypeTextConfig = '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9';
export type TypeTextLetterSpacing = 'x1' | 'x2' | 'x3' | 'x4' | 'x5';
export type TypeLineHeight = '1' | 'x1' | 'x2' | 'x3' | 'x4' | 'x5' | 'x6' | 'x7' | 'x8';
export type TypeTextSize = 'x1' | 'x2' | 'x3' | 'x4' | 'x5' | 'x6' | 'x7' | 'x8';
export type TypeTextWeight = 'bold' | 'medium';

export interface Props extends BaseProps {
  /**
   * Sets the type of component to be rendered. Can be a
   * string for an HTML tag or a React component.
   * */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component?: string | React.ComponentType<any>;
  /* Horizontal alignment of the child text */
  align?: 'start' | 'middle' | 'end';
  /* Sets how the text should break across lines when reaching the container width */
  breakOn?: 'all' | 'none' | 'word';
  /** @ignore */
  className?: string;
  /* Shortcut for setting the correct combination of props for valid typeography design */
  config?: TypeTextConfig;
  /** Clips overflowing text with an ellipsis */
  ellipsis?: boolean;
  /** Empahsises text with an italic style */
  emphasis?: boolean;
  /** Sets the text to you inline layout */
  inline?: boolean;
  /** Adjusts the texts letter spacing to one of the curated values */
  letterSpacing?: TypeTextLetterSpacing;
  /** Adjusts the line height to one of the curated values */
  lineHeight?: TypeLineHeight;
  /** Sets the text to use a monospaced font family */
  monospace?: boolean;
  /** Adjusts the texts sizing to one of the curated values */
  size?: TypeTextSize;
  /** Sets the text to use title casing (first letter of each word is uppercase) */
  titlecase?: boolean;
  /** Sets the text to use uppercasing (every letter of each word is uppercase) */
  uppercase?: boolean;
  /** Adjusts the text weighting */
  weight?: TypeTextWeight;
}

const ComponentMap = {
  x1: 'div',
  x2: 'div',
  x3: 'div',
  x4: 'h5',
  x5: 'h4',
  x6: 'h3',
  x7: 'h2',
  x8: 'h1',
};

const InlineComponentMap = (props: Props) =>
  (props.weight === 'bold' && 'strong') ||
  (props.emphasis && 'em') ||
  'span';

/**
 * Generic text component that applies a wide variety of textual
 * styling.
 */
const Text: React.FunctionComponent<Props> = (props: Props) => {
  const {
    Component,
    align,
    breakOn,
    className,
    config,
    ellipsis,
    emphasis,
    inline,
    letterSpacing,
    lineHeight,
    monospace,
    size,
    titlecase,
    uppercase,
    weight,
    ...rest
  } = props;

  const classes = classnames(className, 'Text', {
    'Text--ellipsis': ellipsis,
    'Text--emphasis': emphasis,
    'Text--monospace': monospace,
    'Text--titlecase': titlecase,
    'Text--uppercase': uppercase,
    [`Text--align-${align}`]: align,
    [`Text--break-${breakOn}`]: breakOn,
    [`Text--config-${config}`]: config,
    [`Text--letter-spacing-${letterSpacing}`]: letterSpacing,
    [`Text--line-height-${lineHeight}`]: lineHeight,
    [`Text--size-${size}`]: size,
    [`Text--weight-${weight}`]: weight,
  });

  const component = Component || (inline
    ? InlineComponentMap(props)
    : (size ? ComponentMap[size] : 'div'));

  return (
    <Base { ...rest }
        Component={ component }
        className={ classes } />
  );
};

export default Text;
